class personalisationDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#PersonalisationDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.initDrawer();
    this.optionDropdown();
  }

  initDrawer() {
    this.add = document.querySelector('[js-personalisation-drawer="add"]');
    this.apply = document.querySelector('[js-personalisation="apply"]');
    this.text = document.querySelectorAll('[js-personalisation="text"]');
    this.textValue = document.querySelectorAll('[js-personalisation="text-value"]');
    this.personalisationValue = document.querySelectorAll('[js-personalisation="personalisation-value"]');
    this.options = document.querySelectorAll('[js-personalisation="option"]');
    this.optionValues = document.querySelectorAll('[js-personalisation="option-value"]');
    this.finishValue = document.querySelectorAll('[js-personalisation="finish-option"]');
    this.discardChanges = document.querySelector('[js-personalisation="discard"]');
    this.addText = document.querySelector('[js-personalisation="cta-text"]');
    this.reset = document.querySelector('[js-personalisation-drawer="reset"]');
    this.optionsWrapper = document.querySelectorAll('[js-personalisation="option-wrapper"]');
    this.optionButton = document.querySelectorAll('[js-personalisation="button"]');
    this.personalisationOptions = document.querySelectorAll('[js-personalisation="personalisation-options"]');

    this.openDrawer(this.add);
    this.checkApplyButtonState();

    this.apply.addEventListener('click', () => this.applyPersonalisation());
    this.discardChanges.addEventListener('click', () => this.resetPersonalisation());
    this.text.forEach((textField) => {
      const counter = textField
        .closest('.personalisation-drawer__field-option')
        .querySelector('[js-personalisation="counter"]');
      textField.addEventListener('input', () => {
        textField.value = textField.value.toUpperCase();
        this.updateCharacterCounter(textField, counter);
        this.checkApplyButtonState();
      });
      textField.addEventListener('blur', () => {
        textField.value = textField.value.toUpperCase();
      });      
    });
    this.options.forEach((option) => {
      option.addEventListener('change', (event) => {
        this.optionsWrapper.forEach((wrapper) => {
          wrapper.classList.remove('is-active');
        });

        if (event.target.checked) {
          event.target.closest('[js-personalisation="option-wrapper"]').classList.add('is-active');
        }
        this.checkApplyButtonState();
      });
    });
    this.reset.addEventListener('click', () => this.resetPersonalisation());
    document.addEventListener('click', (event) => {
      const clickedOutside = ![...this.optionButton, ...this.options].some((element) => element.contains(event.target));

      if (clickedOutside) {
        this.personalisationOptions.forEach((option) => {
          option.style.display = 'none';
          option
            .closest('.personalisation-drawer__option-block')
            ?.querySelector('[js-personalisation="button"]')
            ?.classList.remove('is-active');
        });
      }
    });
  }

  openDrawer(button) {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(button);
    });
    button.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(button);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /**
   * Apply selected personalisation text to the product input props.
   */
  applyPersonalisation() {
    const textFinishUnavailableOrSelected =
      this.options.length === 0 || Array.from(this.options).some((option) => option.checked);

    if (!textFinishUnavailableOrSelected) {
      if (!this.querySelector('.personalisation-drawer__finish .error')) {
        const errorMessage = document.createElement('span');
        errorMessage.classList.add('error');
        errorMessage.innerHTML = this.querySelector('.personalisation-drawer__finish')?.dataset.errorMessage;
        this.querySelector('.personalisation-drawer__finish')?.append(errorMessage);
      }
      this.querySelector('.personalisation-drawer__finish')?.scrollIntoView();
    } else {
      if (this.querySelector('.personalisation-drawer__finish .error')) {
        this.querySelector('.personalisation-drawer__finish .error').remove();
      }

      this.text.forEach((text) => {
        const correspondingTextValues = Array.from(this.textValue).filter((value) => value.id === text.id);
        correspondingTextValues.forEach((correspondingTextValue) => {
          correspondingTextValue.value = text.value;
        });
      });

      this.optionButton.forEach((button) => {
        const correspondingInput = Array.from(this.personalisationValue).find((input) => input.id === button.name);
        if (correspondingInput) {
          correspondingInput.value = button.value;
        }
      });

      const selectedOption = Array.from(this.options).find((option) => option.checked);
      if (selectedOption) {
        this.finishValue.forEach((element) => {
          element.value = selectedOption.value;
        });
      }

      this.close();
      this.updatePersonalisationStatus(true);
    }
  }

  /**
   * Update character counter text.
   * @param {Object} textField - Input text field.
   * @param {Object} counter - Counter HTML.
   */
  updateCharacterCounter(textField, counter) {
    if (!counter) return;
    counter.innerHTML = `${textField.value.length}/${textField.getAttribute('maxlength')} ${window.personalisationStrings.characterString}`;
  }

  /**
   * Update apply button state.
   */
  checkApplyButtonState() {
    let atLeastOneTextFilled = this.text.length === 0 || Array.from(this.text).some((text) => text.value.trim() !== '');
    const allDropdownsSelected = Array.from(this.personalisationOptions).every((optionGroup) => {
      return Array.from(optionGroup.querySelectorAll('[js-personalisation="option-value"]')).some(
        (option) => option.checked
      );
    });

    if (this.personalisationOptions.length !== 0) atLeastOneTextFilled = true;
    this.apply.disabled = !(atLeastOneTextFilled && allDropdownsSelected);
  }

  resetPersonalisation() {
    this.text.forEach((textField) => {
      const counter = textField
        .closest('.personalisation-drawer__field-option')
        .querySelector('[js-personalisation="counter"]');
      textField.value = '';

      if (counter) {
        counter.innerHTML = `0/${textField.getAttribute('maxlength')} ${window.personalisationStrings.characterString}`;
      }
    });

    this.optionButton.forEach((button) => {
      const buttonPlaceholder = button.querySelector('[js-personalisation="button-placeholder"]');
      if (buttonPlaceholder.classList.contains('hidden')) {
        button.querySelector('[js-personalisation="selected-value"]').classList.add('hidden');
        buttonPlaceholder.classList.remove('hidden');
      }
      button.value = '';
    });

    this.options.forEach((option) => {
      option.checked = false;
    });
    this.optionValues.forEach((value) => {
      value.checked = false;
    });
    this.optionsWrapper.forEach((wrapper) => {
      wrapper.classList.remove('is-active');
    });
    this.textValue.forEach((element) => {
      element.value = '';
    });
    this.finishValue.forEach((element) => {
      element.value = '';
    });
    this.querySelector('.personalisation-drawer__finish .error')?.remove();
    this.checkApplyButtonState();
    this.updatePersonalisationStatus(false);
  }

  /** Update personalisation button.
   *
   * @param {Boolean} isEdit
   */
  updatePersonalisationStatus(isEdit) {
    this.addText.innerHTML = isEdit
      ? window.personalisationStrings.editPersonalisation
      : window.personalisationStrings.addPersonalisation;
    this.reset.classList.toggle('hidden', !isEdit);
    this.add.classList.toggle('is-edit', isEdit);
  }

  optionDropdown() {
    this.optionButton.forEach((button, index) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.personalisationOptions.forEach((option, i) => {
          if (i !== index) {
            option.style.display = 'none';
            this.optionButton[i].classList.remove('is-active');
          }
        });

        const isVisible = this.personalisationOptions[index].style.display === 'flex';
        this.personalisationOptions[index].style.display = isVisible ? 'none' : 'flex';
        button.classList.toggle('is-active', !isVisible);
        this.optionListener(this.personalisationOptions[index], button);
      });
    });
  }

  optionListener(optionElement, button) {
    optionElement.querySelectorAll('[js-personalisation="option-value"]').forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        if (!checkbox.checked) return;
        this.updateButtonText(checkbox, button);
      });
    });
  }

  updateButtonText(checkbox, button) {
    button.querySelector('[js-personalisation="selected-value"]').innerHTML = checkbox.value;
    button.value = checkbox.dataset.personalisationName;
    button.querySelector('[js-personalisation="selected-value"]').classList.remove('hidden');
    button.querySelector('[js-personalisation="button-placeholder"]').classList.add('hidden');
    checkbox.closest('[js-personalisation="personalisation-options"]').style.display = 'none';
    if (button.classList.contains('is-active')) {
      button.classList.remove('is-active');
    }

    this.checkApplyButtonState();
  }
}

customElements.define('personalisation-drawer', personalisationDrawer);
